import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Webhook, Loader2, Copy, CheckCircle2, XCircle, ShieldCheck, KeyRound, Link2, Save, RefreshCw, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EVENTS_PAYMENT = ["PAYMENT_RECEIVED", "PAYMENT_CONFIRMED", "PAYMENT_OVERDUE", "PAYMENT_REFUNDED", "PAYMENT_DELETED"];
const EVENTS_TRANSFER = ["TRANSFER_CREATED", "TRANSFER_PENDING", "TRANSFER_IN_BANK_PROCESSING", "TRANSFER_DONE", "TRANSFER_FAILED", "TRANSFER_CANCELLED"];
const EVENTS_SUB = ["SUBSCRIPTION_INACTIVATED", "SUBSCRIPTION_DELETED"];

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-wide text-zinc-500">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-zinc-600">{hint}</p>}
    </div>
  );
}

function UrlBox({ label, url, testid }) {
  const copy = () => { navigator.clipboard.writeText(url); toast.success("URL copiada!"); };
  return (
    <div className="bg-[#09090B] border border-white/10 rounded-sm p-3">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1 flex items-center gap-1"><Link2 className="h-3 w-3" />{label}</div>
      <div className="flex items-center gap-2">
        <code data-testid={testid} className="flex-1 text-xs text-[#00F0FF] break-all mono">{url || "Defina a URL pública abaixo"}</code>
        {url && <Button size="sm" variant="outline" onClick={copy} className="rounded-sm bg-transparent border-white/10 h-8 shrink-0 gap-1"><Copy className="h-3.5 w-3.5" />Copiar</Button>}
      </div>
    </div>
  );
}

export default function AdminAsaas() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ env: "sandbox", base_url: "", public_base_url: "", api_key: "", webhook_token: "", transfer_auth_token: "" });
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/admin/integrations/asaas").then((r) => {
    setCfg(r.data);
    setForm((f) => ({ ...f, env: r.data.env || "sandbox", base_url: r.data.base_url || "", public_base_url: r.data.public_base_url || "", api_key: "", webhook_token: "", transfer_auth_token: "" }));
  }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!cfg) return <div className="flex items-center gap-2 text-zinc-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" />Carregando...</div>;

  const pub = (form.public_base_url || "").replace(/\/$/, "");
  const webhookUrl = pub ? `${pub}/api/webhooks/asaas` : "";
  const transferUrl = pub ? `${pub}/api/webhooks/asaas-transfer-auth` : "";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/integrations/asaas", form);
      toast.success(`Configuração salva (${data.updated.length} campo(s)).`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const validate = async () => {
    setValidating(true); setResult(null);
    try {
      const { data } = await api.post("/admin/integrations/asaas/validate");
      setResult(data);
      if (data.api_key?.ok) toast.success("Conexão com o Asaas OK!");
      else toast.error("Falha ao validar a API Key.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setValidating(false); }
  };

  const secretPlaceholder = (fld) => (cfg[fld]?.set ? `${cfg[fld].masked}  (deixe vazio p/ manter)` : "Não configurado");
  const srcBadge = (fld) => {
    const s = cfg[fld]?.source;
    const map = { db: ["Banco (editado)", "border-[#00F0FF]/40 text-[#00F0FF]"], env: ["Ambiente (.env)", "border-white/15 text-zinc-400"], none: ["Ausente", "border-destructive/40 text-destructive"] };
    const [t, c] = map[s] || map.none;
    return <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${c}`}>{t}</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-asaas">
      <div className="flex items-center gap-3 p-4 rounded-sm border border-[#FF4D00]/30 bg-[#FF4D00]/5">
        <Webhook className="h-5 w-5 text-[#FF4D00] shrink-0" />
        <div className="text-sm text-zinc-300">
          Configuração da integração <b>Asaas</b> (pagamentos, assinaturas e repasses Pix). Alterações salvas aqui têm prioridade sobre o <code className="text-[#00F0FF]">.env</code> e passam a valer imediatamente.
          <span className="ml-1 inline-flex items-center gap-1">{cfg.enabled ? <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Ativa</span> : <span className="text-destructive inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />Inativa</span>}</span>
        </div>
      </div>

      {/* URLs de webhook */}
      <div className="space-y-3">
        <h3 className="font-head text-lg font-semibold flex items-center gap-2"><Link2 className="h-5 w-5 text-[#FF4D00]" />URLs para o painel do Asaas</h3>
        <Field label="Domínio público do app (base URL)" hint="Ex.: https://www.michamou.com.br — as URLs de webhook são geradas a partir daqui.">
          <Input data-testid="asaas-public-url" value={form.public_base_url} onChange={(e) => setForm({ ...form, public_base_url: e.target.value })} placeholder="https://www.michamou.com.br" className="bg-[#09090B] border-white/10 rounded-sm" />
        </Field>
        <UrlBox label="Webhook de Pagamentos & Assinaturas" url={webhookUrl} testid="asaas-webhook-url" />
        <UrlBox label="Validação de Saque (autorização de transferência)" url={transferUrl} testid="asaas-transfer-url" />
      </div>

      {/* Credenciais */}
      <div className="space-y-4">
        <h3 className="font-head text-lg font-semibold flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#FF4D00]" />Credenciais & Tokens</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Ambiente">
            <div className="flex gap-2">
              {["sandbox", "production"].map((v) => (
                <button key={v} data-testid={`asaas-env-${v}`} onClick={() => setForm({ ...form, env: v, base_url: v === "production" ? "https://api.asaas.com" : "https://api-sandbox.asaas.com" })}
                  className={`flex-1 py-2 rounded-sm text-sm border transition-colors ${form.env === v ? "border-[#FF4D00]/50 bg-[#FF4D00]/10 text-[#FF4D00]" : "border-white/10 text-zinc-400 hover:text-zinc-200"}`}>
                  {v === "production" ? "Produção" : "Sandbox"}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Base URL da API">
            <Input data-testid="asaas-base-url" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://api.asaas.com" className="bg-[#09090B] border-white/10 rounded-sm" />
          </Field>
        </div>
        <Field label="API Key">
          <div className="flex items-center justify-end mb-1">{srcBadge("api_key")}</div>
          <Input data-testid="asaas-api-key" type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={secretPlaceholder("api_key")} className="bg-[#09090B] border-white/10 rounded-sm" />
        </Field>
        <Field label="Token do Webhook de Pagamentos">
          <div className="flex items-center justify-end mb-1">{srcBadge("webhook_token")}</div>
          <Input data-testid="asaas-webhook-token" type="password" value={form.webhook_token} onChange={(e) => setForm({ ...form, webhook_token: e.target.value })} placeholder={secretPlaceholder("webhook_token")} className="bg-[#09090B] border-white/10 rounded-sm" />
        </Field>
        <Field label="Token da Validação de Saque">
          <div className="flex items-center justify-end mb-1">{srcBadge("transfer_auth_token")}</div>
          <Input data-testid="asaas-transfer-token" type="password" value={form.transfer_auth_token} onChange={(e) => setForm({ ...form, transfer_auth_token: e.target.value })} placeholder={secretPlaceholder("transfer_auth_token")} className="bg-[#09090B] border-white/10 rounded-sm" />
        </Field>

        <div className="flex flex-wrap gap-3">
          <Button data-testid="asaas-save" onClick={save} disabled={saving} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salvar configuração</Button>
          <Button data-testid="asaas-validate" onClick={validate} disabled={validating} variant="outline" className="rounded-sm bg-transparent border-white/10 gap-2">{validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Validar conexão</Button>
          <Button variant="ghost" onClick={load} className="rounded-sm gap-2 text-zinc-400"><RefreshCw className="h-4 w-4" />Recarregar</Button>
        </div>

        {result && (
          <div data-testid="asaas-validate-result" className={`rounded-sm border p-4 text-sm ${result.api_key?.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-destructive/40 bg-destructive/5"}`}>
            <div className="flex items-center gap-2 font-medium">
              {result.api_key?.ok ? <><CheckCircle2 className="h-4 w-4 text-emerald-400" />API Key válida</> : <><XCircle className="h-4 w-4 text-destructive" />API Key inválida</>}
            </div>
            {result.api_key?.ok
              ? <div className="text-zinc-400 mt-1">Saldo Asaas: <b className="text-foreground">R$ {Number(result.api_key.balance || 0).toFixed(2)}</b> · Ambiente: {result.env}</div>
              : <div className="text-destructive mt-1">{result.api_key?.error}</div>}
            <div className="text-zinc-500 mt-2 text-xs flex flex-wrap gap-3">
              <span>{result.webhook_token ? "✓" : "✗"} Token do webhook de pagamentos</span>
              <span>{result.transfer_auth_token ? "✓" : "✗"} Token da validação de saque</span>
            </div>
          </div>
        )}
      </div>

      {/* Passo a passo */}
      <div className="space-y-3">
        <h3 className="font-head text-lg font-semibold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-warning" />Passo a passo no painel do Asaas</h3>
        <ol className="list-decimal list-inside text-sm text-zinc-400 space-y-2 bg-[#09090B] border border-white/10 rounded-sm p-4">
          <li>Asaas → <b className="text-zinc-200">Integrações → Webhooks</b> → adicione a URL <b>Webhook de Pagamentos</b> acima e cole o <b>Token do Webhook de Pagamentos</b>.</li>
          <li>Marque os eventos: <span className="text-zinc-300">{EVENTS_PAYMENT.join(", ")}, {EVENTS_TRANSFER.join(", ")}, {EVENTS_SUB.join(", ")}</span>. Versão da API v3, formato JSON.</li>
          <li>Asaas → <b className="text-zinc-200">Transferências → Validação de saque via Webhook</b> → cole a URL <b>Validação de Saque</b> e o <b>Token da Validação de Saque</b>.</li>
          <li>Volte aqui e clique em <b className="text-zinc-200">Validar conexão</b> para confirmar a API Key e o saldo.</li>
        </ol>
      </div>
    </div>
  );
}
